import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Pressable,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { useLocalSearchParams, useRouter, useFocusEffect } from 'expo-router';
import DatePickerModal from '../../../src/components/DatePickerModal';
import { showAlert, showConfirm } from '../../../src/utils/alert';
import { requireConnectivity } from '../../../src/utils/connectivity';
import { activitiesApi, goalsApi, visionsApi, recurrenceSchedulesApi, Activity, Goal, Vision, RecurrenceSchedule } from '../../../src/api/client';
import { entityMutation } from '../../../src/api/queryClient';
import { deriveActivityStatus } from '../../../src/utils/displayStatus';
import EditModeModal from '../../../src/components/EditModeModal';
import { rruleToLabel } from '../../../src/utils/recurrence';
import { encrypt, decrypt, encryptTitle, mergeEncryptionMetadata, decryptEntityTitle, getKeyForTeam } from '../../../src/utils/encryption';
import { ACTIVITY_STATUS_COLORS } from '../../../src/theme';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';
import AiSuggestionsCard from '../../../src/components/AiSuggestionsCard';
import AiUnavailableBanner from '../../../src/components/AiUnavailableBanner';
import { useOnDeviceLlm } from '../../../src/hooks/useOnDeviceLlm';
import type { SuggestionItem, SubActivityItem } from '../../../src/services/llm';
import { formatMinutes, formatDisplayDate } from '../../../src/utils/format';
import ScreenHeader from '../../../src/components/ScreenHeader';

const STATUS_OPTIONS = [
  { key: 'todo', label: 'Todo', icon: '\u25CB' },
  { key: 'in-progress', label: 'In Progress', icon: '\u25D0' },
  { key: 'done', label: 'Done', icon: '\u25CF' },
  { key: 'skipped', label: 'Skipped', icon: '\u2298' },
] as const;

const createStyles = (theme: Theme) => ({
  container: { flex: 1, backgroundColor: theme.colors.bgSecondary },
  centered: { flex: 1, justifyContent: 'center' as const, alignItems: 'center' as const, backgroundColor: theme.colors.bgSecondary },
  content: { flex: 1 },
  contentInner: { padding: 16, paddingBottom: 40 },
  title: { fontSize: 24, fontWeight: '700' as const, color: theme.colors.textPrimary, marginBottom: 20 },
  titleInput: { fontSize: 24, fontWeight: '700' as const, color: theme.colors.textPrimary, marginBottom: 20, borderBottomWidth: 2, borderBottomColor: theme.colors.primary, paddingBottom: 8 },
  sectionLabel: { fontSize: 12, fontWeight: '700' as const, color: theme.colors.textMuted, letterSpacing: 0.5, marginBottom: 8, marginTop: 16 },
  statusRow: { flexDirection: 'row' as const, flexWrap: 'wrap' as const, gap: 8 },
  statusChip: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8, backgroundColor: theme.colors.bgTertiary },
  statusChipText: { fontSize: 13, fontWeight: '600' as const, color: theme.colors.textMuted },
  infoCard: { backgroundColor: theme.colors.background, borderRadius: 12, padding: 16, ...theme.shadows.sm },
  infoRow: { flexDirection: 'row' as const, justifyContent: 'space-between' as const, alignItems: 'center' as const, paddingVertical: 4 },
  infoLabel: { fontSize: 14, fontWeight: '500' as const, color: theme.colors.textMuted },
  infoValue: { fontSize: 14, fontWeight: '600' as const, color: theme.colors.textPrimary },
  infoText: { fontSize: 15, color: theme.colors.textSecondary },
  divider: { height: 1, backgroundColor: theme.colors.bgTertiary, marginVertical: 10 },
  descriptionText: { fontSize: 15, color: theme.colors.textSecondary, lineHeight: 22 },
  descriptionInput: { backgroundColor: theme.colors.background, borderRadius: 12, padding: 16, fontSize: 15, color: theme.colors.textPrimary, minHeight: 120, borderWidth: 1, borderColor: theme.colors.border },
  selector: { flexDirection: 'row' as const, alignItems: 'center' as const, justifyContent: 'space-between' as const, backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 14 },
  selectorText: { fontSize: 15, color: theme.colors.textSecondary, flex: 1 },
  chevron: { fontSize: 12, color: theme.colors.textDisabled, marginLeft: 8 },
  pickerList: { backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, marginTop: 4, overflow: 'hidden' as const },
  pickerItem: { paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: theme.colors.borderLight },
  pickerItemActive: { backgroundColor: theme.colors.primaryBg },
  pickerItemText: { fontSize: 14, color: theme.colors.textSecondary },
  dateLabel: { fontSize: 13, fontWeight: '500' as const, color: theme.colors.textSecondary, marginBottom: 6 },
  aiRow: { flexDirection: 'row' as const, gap: 8, marginTop: 16 },
  aiButton: { backgroundColor: theme.colors.aiBg, borderWidth: 1, borderColor: theme.colors.aiBorder, borderRadius: 10, paddingVertical: 12, alignItems: 'center' as const },
  aiButtonText: { fontSize: 15, fontWeight: '600' as const, color: theme.colors.ai },
});

export default function ActivityDetailScreen() {
  const { id, scheduleId, occurrenceTime } = useLocalSearchParams<{ id: string; scheduleId?: string; occurrenceTime?: string }>();
  const isVirtual = id?.startsWith('virtual-') ?? false;
  const router = useRouter();
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();
  const [activity, setActivity] = useState<Activity | null>(null);
  const [goal, setGoal] = useState<Goal | null>(null);
  const [vision, setVision] = useState<Vision | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isSaving, setSaving] = useState(false);
  const [goalTitle, setGoalTitle] = useState('');
  const [visionTitle, setVisionTitle] = useState('');
  const [estimatedMinutes, setEstimatedMinutes] = useState<number | null>(null);
  const [schedule, setSchedule] = useState<RecurrenceSchedule | null>(null);
  const [showEditModeModal, setShowEditModeModal] = useState(false);
  const [showDeleteModeModal, setShowDeleteModeModal] = useState(false);
  const [pendingEditMode, setPendingEditMode] = useState<'this' | 'future' | null>(null);
  const [editGoalId, setEditGoalId] = useState<string | undefined>();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [goalTitles, setGoalTitles] = useState<Record<string, string>>({});
  const [showGoalPicker, setShowGoalPicker] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [editScheduledDate, setEditScheduledDate] = useState('');
  const [editScheduledTime, setEditScheduledTime] = useState('');
  const llm = useOnDeviceLlm();
  const [aiSuggestions, setAiSuggestions] = useState<SuggestionItem[]>([]);
  const [aiSubactivities, setAiSubactivities] = useState<SubActivityItem[]>([]);
  const [showAiResults, setShowAiResults] = useState(false);

  useFocusEffect(
    useCallback(() => {
      if (!id || id === 'create') return;
      fetchActivity();
    }, [id])
  );

  /** Materialize a virtual activity and return the real activity. */
  const materializeVirtual = async (status?: string): Promise<Activity | null> => {
    if (!scheduleId || !occurrenceTime) return null;
    try {
      const res = await entityMutation(() =>
        recurrenceSchedulesApi.materialize(scheduleId, {
          occurrence_time: occurrenceTime,
          ...(status ? { status } : {}),
        }),
      );
      return res.data;
    } catch {
      showAlert('Error', 'Failed to materialize activity.');
      return null;
    }
  };

  const fetchActivity = async () => {
    setIsLoading(true);
    try {
      let activityData: Activity;

      if (isVirtual && scheduleId) {
        // Virtual activity: load template from schedule
        const schedRes = await recurrenceSchedulesApi.get(scheduleId);
        setSchedule(schedRes.data);

        if (!schedRes.data.activity_template_id) {
          showAlert('Error', 'Template not found for this schedule.', () => router.back());
          return;
        }
        const templateRes = await activitiesApi.get(schedRes.data.activity_template_id);
        // Construct a virtual activity view from template
        activityData = {
          ...templateRes.data,
          id: id!,
          start_time: occurrenceTime || templateRes.data.start_time,
          status: 'todo',
          recurrence_schedule_id: scheduleId,
          is_exception: false,
          completed_at: null,
        };
      } else {
        const res = await activitiesApi.get(id!);
        // todo/in-progress is derived from start_time vs now, never written by
        // the app on its own — see displayStatus.ts.
        activityData = { ...res.data, status: deriveActivityStatus(res.data) };
      }

      setActivity(activityData);
      // Decrypt title
      const plainTitle = await decryptEntityTitle(activityData);
      setEditTitle(plainTitle);
      // Decrypt description
      if (activityData.description_encrypted && activityData.encryption_metadata?.iv) {
        try {
          const key = await getKeyForTeam(activityData.team_id);
          const plainDesc = await decrypt(activityData.description_encrypted, { iv: activityData.encryption_metadata.iv }, key);
          setEditDescription(plainDesc);
        } catch { setEditDescription(''); }
      } else {
        setEditDescription(activityData.description_encrypted || '');
      }
      // Decrypt estimated minutes
      if (activityData.estimated_minutes_encrypted && activityData.encryption_metadata?.estimated_minutes_iv) {
        try {
          const key = await getKeyForTeam(activityData.team_id);
          const plainMin = await decrypt(activityData.estimated_minutes_encrypted, { iv: activityData.encryption_metadata.estimated_minutes_iv }, key);
          setEstimatedMinutes(parseInt(plainMin, 10));
        } catch { setEstimatedMinutes(null); }
      }
      // Fetch recurrence schedule if linked (and not already loaded for virtual)
      if (!isVirtual && activityData.recurrence_schedule_id) {
        try {
          const schedRes = await recurrenceSchedulesApi.get(activityData.recurrence_schedule_id);
          setSchedule(schedRes.data);
        } catch { /* schedule may have been deleted */ }
      }
      if (activityData.goal_id) {
        try {
          const goalRes = await goalsApi.get(activityData.goal_id);
          setGoal(goalRes.data);
          setGoalTitle(await decryptEntityTitle(goalRes.data));
          if (goalRes.data.vision_id) {
            try {
              const visionRes = await visionsApi.get(goalRes.data.vision_id);
              setVision(visionRes.data);
              setVisionTitle(await decryptEntityTitle(visionRes.data));
            } catch {}
          }
        } catch {}
      }
    } catch {
      showAlert('Error', 'Failed to load activity.', () => router.back());
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (status: string) => {
    if (!activity) return;
    try {
      if (isVirtual) {
        // Materialize with the desired status
        const real = await materializeVirtual(status);
        if (real) setActivity(real);
      } else {
        await entityMutation(() => activitiesApi.updateStatus(activity.id, status));
        setActivity({ ...activity, status: status as Activity['status'] });
      }
    } catch {
      showAlert('Error', 'Failed to update status.');
    }
  };

  const handleSave = async () => {
    if (!requireConnectivity()) return;
    if (!activity || !editTitle.trim()) return;
    setSaving(true);
    try {
      let targetActivity = activity;

      // For virtual activities with "this" edit mode, materialize first
      if (isVirtual && pendingEditMode !== 'future') {
        const real = await materializeVirtual();
        if (!real) { setSaving(false); return; }
        targetActivity = real;
        setActivity(real);
      }

      const key = await getKeyForTeam(targetActivity.team_id);
      const { title_encrypted, title_iv } = await encryptTitle(editTitle.trim(), key);
      let descEncrypted: string | undefined;
      let descIv: string | undefined;
      if (editDescription.trim()) {
        const descResult = await encrypt(editDescription.trim(), key);
        descEncrypted = descResult.encrypted;
        descIv = descResult.metadata.iv;
      }

      // For "future" edits on virtual activities, update the template directly
      const editId = (isVirtual && pendingEditMode === 'future' && schedule?.activity_template_id)
        ? schedule.activity_template_id
        : targetActivity.id;

      // Build start_time from edited date + time
      let newStartTime: string | undefined;
      if (editScheduledDate && editScheduledTime) {
        const offset = new Date().getTimezoneOffset();
        const sign = offset <= 0 ? '+' : '-';
        const absOff = Math.abs(offset);
        const hh = String(Math.floor(absOff / 60)).padStart(2, '0');
        const mm = String(absOff % 60).padStart(2, '0');
        newStartTime = `${editScheduledDate}T${editScheduledTime}:00${sign}${hh}:${mm}`;
      }

      const res = await entityMutation(() =>
        activitiesApi.update(editId, {
          title_encrypted,
          description_encrypted: descEncrypted,
          encryption_metadata: mergeEncryptionMetadata(descIv, title_iv),
          goal_id: editGoalId ?? null,
          start_time: newStartTime,
          ...(pendingEditMode ? { edit_mode: pendingEditMode } : {}),
        }),
      );
      setActivity(res.data);
      // Update local goal/vision state
      if (editGoalId) {
        try {
          const goalRes = await goalsApi.get(editGoalId);
          setGoal(goalRes.data);
          setGoalTitle(await decryptEntityTitle(goalRes.data));
          if (goalRes.data.vision_id) {
            const visionRes = await visionsApi.get(goalRes.data.vision_id);
            setVision(visionRes.data);
            setVisionTitle(await decryptEntityTitle(visionRes.data));
          } else {
            setVision(null);
            setVisionTitle('');
          }
        } catch {}
      } else {
        setGoal(null);
        setGoalTitle('');
        setVision(null);
        setVisionTitle('');
      }
      setIsEditing(false);
      // If "this" mode was used, activity is now an exception — clear schedule
      if (pendingEditMode === 'this') {
        setSchedule(null);
      }
      setPendingEditMode(null);
    } catch {
      showAlert('Error', 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleClone = async () => {
    if (!activity) return;
    try {
      const cloneTitle = `${editTitle} (copy)`;
      const key = await getKeyForTeam(activity.team_id);
      const { title_encrypted, title_iv } = await encryptTitle(cloneTitle, key);
      await entityMutation(() =>
        activitiesApi.create({
          title_encrypted,
          description_encrypted: activity.description_encrypted || undefined,
          encryption_metadata: mergeEncryptionMetadata(activity.encryption_metadata?.iv, title_iv, {
            ...(activity.encryption_metadata?.estimated_minutes_iv && { estimated_minutes_iv: activity.encryption_metadata.estimated_minutes_iv }),
          }),
          goal_id: activity.goal_id || undefined,
          estimated_minutes_encrypted: activity.estimated_minutes_encrypted || undefined,
          start_time: activity.start_time,
        }),
      );
      showAlert('Cloned', 'Activity duplicated successfully.', () => router.back());
    } catch {
      showAlert('Error', 'Failed to clone activity.');
    }
  };

  const initEditState = () => {
    setEditGoalId(activity?.goal_id ?? undefined);
    if (activity?.start_time) {
      const d = new Date(activity.start_time);
      setEditScheduledDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
      setEditScheduledTime(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
    }
    setShowGoalPicker(false);
    setShowTimePicker(false);
    // Fetch goals list for picker
    goalsApi.list().then(async (res) => {
      setGoals(res.data);
      const entries = await Promise.all(
        res.data.map(async (g): Promise<[string, string]> => [g.id, await decryptEntityTitle(g)])
      );
      setGoalTitles(Object.fromEntries(entries));
    }).catch(() => {});
  };

  const handleEditPress = () => {
    if (isVirtual || activity?.recurrence_schedule_id) {
      setShowEditModeModal(true);
    } else {
      initEditState();
      setIsEditing(true);
    }
  };

  const handleEditModeSelect = (action: 'this' | 'future' | 'cancel') => {
    setShowEditModeModal(false);
    if (action === 'cancel') return;
    initEditState();
    setPendingEditMode(action);
    setIsEditing(true);
  };

  const handleAiSuggest = async () => {
    if (!editTitle.trim()) return;
    setShowAiResults(true);
    setAiSuggestions([]);
    setAiSubactivities([]);
    const result = await llm.suggest('activity', `${editTitle.trim()}: ${editDescription.trim()}`);
    if (result) {
      setAiSuggestions(result.suggestions);
    }
  };

  const handleAiSplit = async () => {
    if (!editTitle.trim()) return;
    setShowAiResults(true);
    setAiSuggestions([]);
    setAiSubactivities([]);
    const result = await llm.splitActivity(editTitle.trim(), estimatedMinutes ?? 60);
    if (result) {
      setAiSubactivities(result.suggested_activities);
    }
  };

  const handleDelete = () => {
    if (!requireConnectivity()) return;
    if (isVirtual || activity?.recurrence_schedule_id) {
      setShowDeleteModeModal(true);
    } else {
      confirmDelete();
    }
  };

  const handleDeleteModeSelect = (action: 'this' | 'future' | 'cancel') => {
    setShowDeleteModeModal(false);
    if (action === 'cancel') return;
    confirmDelete(action);
  };

  const confirmDelete = (deleteMode?: 'this' | 'future') => {
    const message = deleteMode === 'future'
      ? `Delete "${editTitle}" and stop all future occurrences?`
      : `Are you sure you want to delete "${editTitle}"?`;
    showConfirm(
      'Delete Activity',
      message,
      async () => {
        try {
          if (isVirtual && deleteMode === 'future' && scheduleId) {
            // Deactivate the schedule — no need to materialize
            await entityMutation(() =>
              recurrenceSchedulesApi.update(scheduleId, {
                is_active: false,
                schedule_end: occurrenceTime,
              }),
            );
          } else if (isVirtual) {
            // "this" delete: materialize then delete (materializeVirtual already wraps)
            const real = await materializeVirtual();
            if (real) await entityMutation(() => activitiesApi.delete(real.id, deleteMode));
          } else {
            await entityMutation(() => activitiesApi.delete(activity!.id, deleteMode));
          }
          router.back();
        } catch {
          showAlert('Error', 'Failed to delete activity.');
        }
      },
      { confirmText: 'Delete', destructive: true }
    );
  };

  if (isLoading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (!activity) return null;

  const statusStyle = ACTIVITY_STATUS_COLORS[activity.status] || ACTIVITY_STATUS_COLORS.todo;

  return (
    <View style={styles.container}>
      <ScreenHeader
        title="Activity Details"
        left={
          isEditing
            ? {
                label: 'Cancel editing',
                text: 'Cancel',
                onPress: () => setIsEditing(false),
                testID: 'activity-detail.back-button',
              }
            : {
                label: 'Go back',
                icon: 'arrow-back',
                onPress: () => router.back(),
                testID: 'activity-detail.back-button',
              }
        }
        right={
          isEditing
            ? {
                label: 'Save changes',
                text: 'Save',
                tone: 'primary',
                onPress: handleSave,
                disabled: isSaving,
                loading: isSaving,
                testID: 'activity-detail.save-button',
              }
            : [
                {
                  label: 'Clone activity',
                  icon: 'copy-outline',
                  onPress: handleClone,
                  testID: 'activity-detail.clone-button',
                },
                {
                  label: 'Edit activity',
                  icon: 'create-outline',
                  onPress: handleEditPress,
                  testID: 'activity-detail.edit-button',
                },
                {
                  label: 'Delete activity',
                  icon: 'trash-outline',
                  tone: 'danger',
                  accessibilityHint: 'Permanently deletes this activity',
                  onPress: handleDelete,
                  testID: 'activity-detail.delete-button',
                },
              ]
        }
      />
      <ScrollView style={styles.content} contentContainerStyle={styles.contentInner}>
        {/* Title */}
        {isEditing ? (
          <TextInput
            style={styles.titleInput}
            value={editTitle}
            onChangeText={setEditTitle}
            placeholder="Activity title"
            autoFocus
            accessibilityLabel="Activity title"
            testID="activity-detail.title-input"
          />
        ) : (
          <Text style={styles.title}>{editTitle}</Text>
        )}

        {/* Status */}
        <Text style={styles.sectionLabel}>STATUS</Text>
        <View style={styles.statusRow} accessibilityRole="radiogroup" accessibilityLabel="Activity status">
          {STATUS_OPTIONS.map((opt) => (
            <Pressable
              key={opt.key}
              style={[
                styles.statusChip,
                activity.status === opt.key && { backgroundColor: ACTIVITY_STATUS_COLORS[opt.key].bg },
              ]}
              onPress={() => handleStatusChange(opt.key)}
              accessibilityRole="button"
              accessibilityState={{ checked: activity.status === opt.key, selected: activity.status === opt.key }}
              aria-checked={activity.status === opt.key}
              aria-selected={activity.status === opt.key}
              accessibilityLabel={opt.label}
              testID={`activity-detail.status-${opt.key}`}
            >
              <Text style={[
                styles.statusChipText,
                activity.status === opt.key && { color: ACTIVITY_STATUS_COLORS[opt.key].text },
              ]} accessible={false}>
                {opt.icon} {opt.label}
              </Text>
            </Pressable>
          ))}
        </View>

        {/* Linked Goal */}
        <Text style={styles.sectionLabel}>LINKED GOAL</Text>
        {isEditing ? (
          <>
            <TouchableOpacity
              style={styles.selector}
              onPress={() => setShowGoalPicker(!showGoalPicker)}
              accessibilityLabel={editGoalId ? `Linked goal: ${goalTitles[editGoalId] || 'Untitled'}` : 'Select a goal (optional)'}
              accessibilityRole="button"
              testID="activity-detail.goal-selector"
            >
              <Text style={styles.selectorText} accessible={false}>
                {editGoalId ? `\u{1F3AF} ${goalTitles[editGoalId] || 'Untitled'}` : 'Select a goal (optional)'}
              </Text>
              <Text style={styles.chevron} accessible={false}>{showGoalPicker ? '\u25B2' : '\u25BC'}</Text>
            </TouchableOpacity>
            {showGoalPicker && (
              <View style={styles.pickerList}>
                <TouchableOpacity
                  style={[styles.pickerItem, !editGoalId && styles.pickerItemActive]}
                  onPress={() => { setEditGoalId(undefined); setShowGoalPicker(false); }}
                >
                  <Text style={styles.pickerItemText}>None</Text>
                </TouchableOpacity>
                {goals.map((g) => (
                  <TouchableOpacity
                    key={g.id}
                    style={[styles.pickerItem, editGoalId === g.id && styles.pickerItemActive]}
                    onPress={() => { setEditGoalId(g.id); setShowGoalPicker(false); }}
                  >
                    <Text style={styles.pickerItemText} numberOfLines={1}>{goalTitles[g.id] || 'Untitled'}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </>
        ) : (
          <View style={styles.infoCard}>
            <Text style={styles.infoText}>{goal ? `\u{1F3AF} ${goalTitle}` : 'No linked goal'}</Text>
            {vision && (
              <>
                <View style={styles.divider} />
                <Text style={styles.infoText}>{'\u2728'} {visionTitle}</Text>
              </>
            )}
          </View>
        )}

        {/* Scheduling */}
        <Text style={styles.sectionLabel}>SCHEDULING</Text>
        {isEditing ? (
          <>
            <Text style={styles.dateLabel}>Scheduled Date</Text>
            <TouchableOpacity
              style={styles.selector}
              onPress={() => setShowDatePicker(true)}
              accessibilityLabel={`Scheduled date: ${editScheduledDate ? formatDisplayDate(editScheduledDate) : 'Not set'}`}
              accessibilityRole="button"
              testID="activity-detail.date-selector"
            >
              <Text style={styles.selectorText} accessible={false}>
                {editScheduledDate ? formatDisplayDate(editScheduledDate) : 'Not set'}
              </Text>
            </TouchableOpacity>
            <DatePickerModal
              visible={showDatePicker}
              value={editScheduledDate}
              onSelect={(d) => { setEditScheduledDate(d); setShowDatePicker(false); }}
              onClose={() => setShowDatePicker(false)}
              title="Schedule Activity"
            />

            <Text style={[styles.dateLabel, { marginTop: 12 }]}>Start Time</Text>
            <TouchableOpacity
              style={styles.selector}
              onPress={() => setShowTimePicker(!showTimePicker)}
              accessibilityLabel={`Start time: ${(() => {
                const [h, m] = editScheduledTime.split(':').map(Number);
                const ampm = h >= 12 ? 'PM' : 'AM';
                const h12 = h % 12 || 12;
                return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
              })()}`}
              accessibilityRole="button"
              testID="activity-detail.time-selector"
            >
              <Text style={styles.selectorText} accessible={false}>
                {(() => {
                  const [h, m] = editScheduledTime.split(':').map(Number);
                  const ampm = h >= 12 ? 'PM' : 'AM';
                  const h12 = h % 12 || 12;
                  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
                })()}
              </Text>
              <Text style={styles.chevron} accessible={false}>{showTimePicker ? '\u25B2' : '\u25BC'}</Text>
            </TouchableOpacity>
            {showTimePicker && (
              <View style={styles.pickerList}>
                {[6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21].map((h) =>
                  [0, 30].map((m) => {
                    const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                    const ampm = h >= 12 ? 'PM' : 'AM';
                    const h12 = h % 12 || 12;
                    const label = `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
                    return (
                      <TouchableOpacity
                        key={val}
                        style={[styles.pickerItem, editScheduledTime === val && styles.pickerItemActive]}
                        onPress={() => { setEditScheduledTime(val); setShowTimePicker(false); }}
                      >
                        <Text style={styles.pickerItemText}>{label}</Text>
                      </TouchableOpacity>
                    );
                  })
                )}
              </View>
            )}

            <View style={[styles.infoCard, { marginTop: 12 }]}>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Duration</Text>
                <Text style={styles.infoValue}>{formatMinutes(estimatedMinutes) ?? 'Not set'}</Text>
              </View>
              {(schedule || activity.is_exception) && (
                <>
                  <View style={styles.divider} />
                  <View style={styles.infoRow}>
                    <Text style={styles.infoLabel}>Repeat</Text>
                    <Text style={styles.infoValue}>
                      {schedule ? rruleToLabel(schedule.rrule) : 'None (exception)'}
                    </Text>
                  </View>
                </>
              )}
            </View>
          </>
        ) : (
          <View style={styles.infoCard}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Start Time</Text>
              <Text style={styles.infoValue}>
                {activity.start_time ? new Date(activity.start_time).toLocaleString() : 'Not set'}
              </Text>
            </View>
            <View style={styles.divider} />
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Duration</Text>
              <Text style={styles.infoValue}>{formatMinutes(estimatedMinutes) ?? 'Not set'}</Text>
            </View>
            {(schedule || activity.is_exception) && (
              <>
                <View style={styles.divider} />
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Repeat</Text>
                  <Text style={styles.infoValue}>
                    {schedule ? rruleToLabel(schedule.rrule) : 'None (exception)'}
                  </Text>
                </View>
              </>
            )}
            {activity.is_exception && (
              <>
                <View style={styles.divider} />
                <View style={styles.infoRow}>
                  <Text style={[styles.infoLabel, { fontStyle: 'italic' }]}>
                    Modified instance of a recurring activity
                  </Text>
                </View>
              </>
            )}
          </View>
        )}

        {/* Description */}
        <Text style={styles.sectionLabel}>DESCRIPTION</Text>
        {isEditing ? (
          <TextInput
            style={styles.descriptionInput}
            value={editDescription}
            onChangeText={setEditDescription}
            placeholder="Add a description..."
            multiline
            textAlignVertical="top"
            accessibilityLabel="Activity description"
            testID="activity-detail.description-input"
          />
        ) : (
          <View style={styles.infoCard}>
            <Text style={styles.descriptionText}>
              {editDescription || 'No description'}
            </Text>
          </View>
        )}

        {/* AI Suggestions */}
        {llm.available === false && (
          <AiUnavailableBanner reason={llm.unavailableReason} />
        )}
        {llm.available === true && (
          <View style={styles.aiRow}>
            <TouchableOpacity
              style={[styles.aiButton, { flex: 1 }]}
              onPress={handleAiSuggest}
              disabled={llm.loading}
              accessibilityLabel="Get AI suggestions"
              accessibilityRole="button"
              testID="activity-detail.ai-suggest-button"
            >
              <Text style={styles.aiButtonText} accessible={false}>{'\u{1F9E0}'} AI Suggest</Text>
            </TouchableOpacity>
            {estimatedMinutes != null && estimatedMinutes > 90 && (
              <TouchableOpacity
                style={[styles.aiButton, { flex: 1 }]}
                onPress={handleAiSplit}
                disabled={llm.loading}
                accessibilityLabel="Split activity with AI"
                accessibilityRole="button"
                testID="activity-detail.ai-split-button"
              >
                <Text style={styles.aiButtonText} accessible={false}>{'\u2702\uFE0F'} AI Split</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {showAiResults && (
          <AiSuggestionsCard
            loading={llm.loading}
            modelDownloading={llm.modelDownloading}
            suggestions={aiSuggestions}
            subtasks={aiSubactivities}
            error={llm.error}
            onApplySuggestion={(text) => {
              setEditTitle(text);
              setIsEditing(true);
              setShowAiResults(false);
              setAiSuggestions([]);
              setAiSubactivities([]);
            }}
            onAddSubtask={(subtask) => {
              showAlert('Sub-activity Added', `"${subtask.text}" (${subtask.estimated_minutes}m) — create it as a separate activity after saving this one.`);
            }}
            onDismiss={() => {
              setShowAiResults(false);
              setAiSuggestions([]);
              setAiSubactivities([]);
            }}
          />
        )}

      </ScrollView>

      <EditModeModal
        visible={showEditModeModal}
        onSelect={handleEditModeSelect}
      />

      <EditModeModal
        visible={showDeleteModeModal}
        title="Delete Recurring Activity"
        description="How should this deletion be applied?"
        thisLabel="Delete this activity only"
        futureLabel="Delete all future activities"
        onSelect={handleDeleteModeSelect}
        destructive
      />
    </View>
  );
}
